import React, { useEffect, useState } from "react";
import client, { formatApiError } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Code2, Plus, Edit2, Trash2, Play } from "lucide-react";

const CATEGORIES = ["general", "string", "date", "numeric", "conditional", "lookup"];

function FnModal({ fn, onSave, onClose }) {
  const [form, setForm] = useState({ name: fn?.name || "", expression: fn?.expression || "", description: fn?.description || "", test_input: JSON.stringify(fn?.test_input || {}), category: fn?.category || "general" });
  const [testResult, setTestResult] = useState(fn?.test_output || "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const test = async () => {
    try {
      let row = {};
      try { row = JSON.parse(form.test_input); } catch {}
      const res = await client.post("/functions/", { ...form, test_input: row });
      setTestResult(res.data.test_output || "");
    } catch (e) { setTestResult("Error: " + formatApiError(e)); }
  };

  const handleSave = async () => {
    if (!form.name || !form.expression) { setError("Name and expression required"); return; }
    let test_input = {};
    try { test_input = JSON.parse(form.test_input); } catch {}
    setSaving(true);
    try { await onSave({ ...form, test_input }); onClose(); }
    catch (e) { setError(formatApiError(e)); setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>{fn ? "Edit" : "New"} Custom Function</DialogTitle></DialogHeader>
        {error && <div className="p-2 bg-red-50 rounded text-red-700 text-sm">{error}</div>}
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs text-gray-500">Function Name * (auto-uppercased)</Label><Input className="mt-1 font-mono" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value.toUpperCase() }))} placeholder="FULL_NAME" /></div>
            <div>
              <Label className="text-xs text-gray-500">Category</Label>
              <Select value={form.category} onValueChange={v => setForm(f => ({ ...f, category: v }))}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div><Label className="text-xs text-gray-500">Description</Label><Input className="mt-1" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} /></div>
          <div>
            <Label className="text-xs text-gray-500">Expression</Label>
            <textarea className="w-full h-20 px-2 py-1.5 text-sm font-mono border border-gray-200 rounded-md focus:outline-none focus:border-[#006080] mt-1 resize-none" value={form.expression} onChange={e => setForm(f => ({ ...f, expression: e.target.value }))} placeholder='LEFT({first_name}, 1) & ". " & {last_name}' />
          </div>
          <div>
            <Label className="text-xs text-gray-500">Test Input (JSON row)</Label>
            <Input className="mt-1 font-mono text-xs" value={form.test_input} onChange={e => setForm(f => ({ ...f, test_input: e.target.value }))} placeholder='{"first_name": "John", "last_name": "Smith"}' />
            <div className="flex items-center gap-2 mt-1.5">
              <Button size="sm" variant="outline" className="h-6 px-2 text-xs" onClick={test}><Play size={10} className="mr-1" /> Test</Button>
              {testResult && <span className="text-xs font-mono text-green-700 bg-green-50 px-2 py-0.5 rounded">{testResult}</span>}
            </div>
          </div>
        </div>
        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button className="bg-[#006080] hover:bg-[#004d66] text-white" onClick={handleSave} disabled={saving}>{saving ? "Saving..." : "Save Function"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function CustomFunctionsPage() {
  const [fns, setFns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingFn, setEditingFn] = useState(null);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    const res = await client.get("/functions/").catch(() => ({ data: [] }));
    setFns(res.data);
    setLoading(false);
  };

  const save = async (form) => {
    if (editingFn) await client.put(`/functions/${editingFn._id}`, form);
    else await client.post("/functions/", form);
    await load();
  };

  const del = async (id) => {
    if (!window.confirm("Delete function?")) return;
    await client.delete(`/functions/${id}`);
    load();
  };

  return (
    <div className="p-6 max-w-5xl mx-auto fade-in">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="flex items-center gap-2 mb-1"><Code2 size={18} className="text-blue-600" /><h1 className="text-2xl font-bold text-gray-900">Custom Functions</h1></div>
          <p className="text-gray-500 text-sm">Save reusable named expressions. Reference in pipelines with your function name.</p>
        </div>
        <Button className="bg-[#006080] hover:bg-[#004d66] text-white" onClick={() => { setEditingFn(null); setShowModal(true); }}><Plus size={15} className="mr-2" /> New Function</Button>
      </div>
      {loading ? <div className="space-y-2">{[1,2,3].map(i => <div key={i} className="h-16 bg-white border border-gray-200 rounded-lg animate-pulse" />)}</div>
      : fns.length === 0 ? (
        <div className="text-center py-12 bg-white border border-gray-200 rounded-lg">
          <Code2 size={36} className="mx-auto text-gray-200 mb-3" />
          <p className="text-gray-400">No custom functions yet</p>
          <Button className="mt-4 bg-[#006080] text-white" onClick={() => setShowModal(true)}>Create first function</Button>
        </div>
      ) : (
        <div className="space-y-3">
          {fns.map(fn => (
            <div key={fn._id} className="bg-white border border-gray-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 bg-blue-50 rounded-md flex items-center justify-center flex-shrink-0"><Code2 size={18} className="text-blue-600" /></div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <code className="font-bold text-[#006080] text-sm">{fn.name}()</code>
                    <Badge variant="secondary" className="text-xs capitalize">{fn.category}</Badge>
                  </div>
                  {fn.description && <p className="text-xs text-gray-400 mt-0.5">{fn.description}</p>}
                  <code className="text-xs text-gray-500 font-mono mt-1 block truncate">{fn.expression}</code>
                  {fn.test_output && <span className="text-xs text-green-600 mt-0.5">→ {fn.test_output}</span>}
                </div>
                <div className="flex gap-1.5">
                  <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={() => { setEditingFn(fn); setShowModal(true); }}><Edit2 size={11} /></Button>
                  <Button size="sm" variant="outline" className="h-7 px-2 text-xs text-red-500 hover:bg-red-50" onClick={() => del(fn._id)}><Trash2 size={11} /></Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      {showModal && <FnModal fn={editingFn} onSave={save} onClose={() => { setShowModal(false); setEditingFn(null); }} />}
    </div>
  );
}
